import { Ativo, Device, buscarAtivos, buscarDevices, buscarTagsDisponiveis, buscarVariaveis } from "../api/m5";

export interface ValoresSeletor {
  assetIds: string[];
  deviceIds: string[];
  tags: string[];
  inicio?: string;
  fim?: string;
}

export interface OpcoesSeletor {
  /** Tags marcadas por padrao na primeira carga (ex: ['TA']). */
  tagsPadrao: string[];
}

function formatarDatetimeLocal(data: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${data.getFullYear()}-${p(data.getMonth() + 1)}-${p(data.getDate())}T${p(data.getHours())}:${p(data.getMinutes())}`;
}

/**
 * Filtro de dados do Grafico/Tabela: estacao e' o eixo primario, device e
 * variavel ficam num segundo popup - variaveis primeiro, devices depois,
 * porque device quase nunca precisa ser restringido (comeca com todos
 * marcados). Ambos os popups mostram/fecham so' via botao (Fechar/Aplicar),
 * sem logica de "clicar fora" - um dropdown posicionado com deteccao de
 * clique fora se mostrou pouco confiavel (ficava "preso" aberto); popup
 * modal (mesmo padrao ja usado no resto do app) e' mais simples e robusto.
 * O popup de variavel/device so mostra o que existe dentro das estacoes
 * marcadas no momento em que ele e' aberto (filtro em cascata).
 */
export async function montarSeletorDados(
  container: HTMLElement,
  opcoes: OpcoesSeletor,
  aoAplicar: (valores: ValoresSeletor) => void
): Promise<void> {
  const [ativos, devices] = await Promise.all([
    buscarAtivos().catch(() => [] as Ativo[]),
    buscarDevices().catch(() => [] as Device[]),
  ]);
  const nomeVariavelCache = new Map<string, string | null>();
  buscarVariaveis()
    .then((vs) => vs.forEach((v) => nomeVariavelCache.set(v.tag, v.nome)))
    .catch(() => {});

  const assetsMarcados = new Set(ativos.map((a) => a.id)); // default: todas
  const devicesMarcados = new Set(devices.map((d) => d.device_id)); // default: todos
  const tagsMarcadas = new Set(opcoes.tagsPadrao);

  container.innerHTML = `
    <div class="filtros" style="margin-bottom: 12px; align-items: flex-end;">
      <label>Início <input type="datetime-local" id="sd-inicio" /></label>
      <label>Fim <input type="datetime-local" id="sd-fim" /></label>
      <button class="botao secundario" id="sd-estacoes-botao" type="button">Estações: todas</button>
      <button class="botao secundario" id="sd-abrir-modal">Selecionar variáveis / dispositivos</button>
      <button class="botao" id="sd-filtrar">Filtrar</button>
    </div>
    <div class="erro" id="sd-erro" style="color: var(--color-danger, #b00020); font-size: 0.85rem; min-height: 1.2em;"></div>
    <div id="sd-modal"></div>
  `;

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const seteDiasAtras = new Date(hoje);
  seteDiasAtras.setDate(seteDiasAtras.getDate() - 7);
  (container.querySelector("#sd-inicio") as HTMLInputElement).value = formatarDatetimeLocal(seteDiasAtras);
  (container.querySelector("#sd-fim") as HTMLInputElement).value = formatarDatetimeLocal(hoje);

  function fecharModal(): void {
    (container.querySelector("#sd-modal") as HTMLElement).innerHTML = "";
  }

  function secaoChecklist(
    id: string,
    titulo: string,
    itens: { valor: string; rotulo: string }[],
    marcados: Set<string>
  ): string {
    return `
      <div id="${id}" class="modal-secao">
        <div style="display:flex; justify-content:space-between; align-items:center;">
          <strong>${titulo}</strong>
          <span>
            <button type="button" class="botao-link" data-marcar-todos>Marcar todas</button> ·
            <button type="button" class="botao-link" data-desmarcar-todos>Desmarcar todas</button>
          </span>
        </div>
        <div class="checklist" style="max-height: 240px; overflow-y: auto; margin-top: 6px;">
          ${
            itens.length
              ? itens
                  .map(
                    (item) => `
            <label style="display:flex; flex-direction:row; align-items:center; gap:6px; font-weight:normal;">
              <input type="checkbox" value="${item.valor}" ${marcados.has(item.valor) ? "checked" : ""} style="flex-shrink:0;">
              <span>${item.rotulo}</span>
            </label>`
                  )
                  .join("")
              : '<p class="aviso">nada disponível.</p>'
          }
        </div>
      </div>`;
  }

  function ligarSecao(raiz: HTMLElement, id: string): void {
    const secaoEl = raiz.querySelector(`#${id}`) as HTMLElement;
    secaoEl.querySelector("[data-marcar-todos]")!.addEventListener("click", () => {
      secaoEl.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((cb) => (cb.checked = true));
    });
    secaoEl.querySelector("[data-desmarcar-todos]")!.addEventListener("click", () => {
      secaoEl.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((cb) => (cb.checked = false));
    });
  }

  // ---------- Popup de estacoes ----------
  function atualizarLabelEstacoes(): void {
    const botao = container.querySelector("#sd-estacoes-botao") as HTMLElement;
    if (assetsMarcados.size === 0 || assetsMarcados.size === ativos.length) {
      botao.textContent = "Estações: todas";
    } else {
      botao.textContent = `Estações: ${assetsMarcados.size} selecionada(s)`;
    }
  }

  function abrirModalEstacoes(): void {
    const modal = container.querySelector("#sd-modal") as HTMLElement;
    modal.innerHTML = `
      <div class="modal-fundo">
        <div class="modal" style="width: 420px;">
          <h3>Selecionar estações</h3>
          ${secaoChecklist(
            "sd-secao-estacoes",
            "Estações",
            ativos.map((a) => ({ valor: a.id, rotulo: a.nome })),
            assetsMarcados
          )}
          <div class="modal-secao" style="display:flex; gap:8px; justify-content:flex-end;">
            <button class="botao secundario" id="sd-modal-fechar">Fechar</button>
            <button class="botao" id="sd-modal-aplicar">Aplicar</button>
          </div>
        </div>
      </div>`;

    ligarSecao(modal, "sd-secao-estacoes");
    modal.querySelector("#sd-modal-fechar")!.addEventListener("click", fecharModal);
    modal.querySelector("#sd-modal-aplicar")!.addEventListener("click", () => {
      assetsMarcados.clear();
      modal
        .querySelectorAll<HTMLInputElement>("#sd-secao-estacoes input[type=checkbox]:checked")
        .forEach((cb) => assetsMarcados.add(cb.value));
      atualizarLabelEstacoes();
      fecharModal();
    });
  }

  container.querySelector("#sd-estacoes-botao")!.addEventListener("click", abrirModalEstacoes);

  // ---------- Coleta / aplicar ----------
  function coletar(): ValoresSeletor {
    const inicioRaw = (container.querySelector("#sd-inicio") as HTMLInputElement).value;
    const fimRaw = (container.querySelector("#sd-fim") as HTMLInputElement).value;
    return {
      assetIds: assetsMarcados.size === 0 || assetsMarcados.size === ativos.length ? [] : [...assetsMarcados],
      deviceIds: devicesMarcados.size === devices.length ? [] : [...devicesMarcados],
      tags: [...tagsMarcadas],
      inicio: inicioRaw ? new Date(inicioRaw).toISOString() : undefined,
      fim: fimRaw ? new Date(fimRaw).toISOString() : undefined,
    };
  }

  async function aplicarComTratamentoDeErro(): Promise<void> {
    const erroEl = container.querySelector<HTMLElement>("#sd-erro")!;
    erroEl.textContent = "";
    try {
      await aoAplicar(coletar());
    } catch (e) {
      erroEl.textContent = "erro ao filtrar: " + (e instanceof Error ? e.message : String(e));
    }
  }

  // ---------- Popup de variaveis (primeiro) + dispositivos (depois) ----------
  async function abrirModalVariaveisDevices(): Promise<void> {
    const estacoesAtivas = assetsMarcados.size > 0 ? assetsMarcados : new Set(ativos.map((a) => a.id));
    const devicesDisponiveis = devices.filter((d) => estacoesAtivas.has(d.asset_id));
    const nomePorAsset = new Map(ativos.map((a) => [a.id, a.nome]));

    // Tags disponiveis = quais tags essas estacoes ja reportaram alguma vez
    // de verdade (SELECT DISTINCT no M5), nao uma amostra de leituras
    // recentes - amostra escondia variavel gravada com pouca frequencia
    // (ex: uma calculada 1x/dia) quando dominada por tags horarias de varias
    // estacoes. variable_dictionary sozinho nao serve aqui por ser global
    // (mostraria tag de estacao sem nenhuma relacao com a selecionada).
    const tagsDisponiveis = await buscarTagsDisponiveis([...estacoesAtivas]).catch(() => []);

    const modal = container.querySelector("#sd-modal") as HTMLElement;
    modal.innerHTML = `
      <div class="modal-fundo">
        <div class="modal" style="width: 520px;">
          <h3>Selecionar variáveis e dispositivos</h3>
          <p class="aviso">Mostrando só o que está disponível nas estações marcadas. Variável primeiro - dispositivo raramente precisa ser restringido, por isso começa com todos marcados.</p>
          ${secaoChecklist(
            "sd-secao-tags",
            "Variáveis",
            tagsDisponiveis.map((t) => ({
              valor: t,
              rotulo: nomeVariavelCache.get(t) ? `${t} — ${nomeVariavelCache.get(t)}` : t,
            })),
            tagsMarcadas
          )}
          ${secaoChecklist(
            "sd-secao-devices",
            "Dispositivos",
            devicesDisponiveis.map((d) => ({
              valor: d.device_id,
              rotulo: `${d.device_id} (${nomePorAsset.get(d.asset_id) || d.asset_id})`,
            })),
            devicesMarcados
          )}
          <div class="modal-secao" style="display:flex; gap:8px; justify-content:flex-end;">
            <button class="botao secundario" id="sd-modal-fechar">Fechar</button>
            <button class="botao" id="sd-modal-aplicar">Aplicar</button>
          </div>
        </div>
      </div>`;

    ligarSecao(modal, "sd-secao-tags");
    ligarSecao(modal, "sd-secao-devices");

    modal.querySelector("#sd-modal-fechar")!.addEventListener("click", fecharModal);
    modal.querySelector("#sd-modal-aplicar")!.addEventListener("click", () => {
      tagsMarcadas.clear();
      modal
        .querySelectorAll<HTMLInputElement>("#sd-secao-tags input[type=checkbox]:checked")
        .forEach((cb) => tagsMarcadas.add(cb.value));
      // Devices: so' atualiza os que estavam visiveis neste popup (escopo das
      // estacoes atuais) - marcacoes de outras estacoes, feitas antes de
      // mudar a selecao de estacoes, continuam guardadas sem aparecer aqui.
      const idsVisiveis = new Set(devicesDisponiveis.map((d) => d.device_id));
      for (const id of idsVisiveis) devicesMarcados.delete(id);
      modal
        .querySelectorAll<HTMLInputElement>("#sd-secao-devices input[type=checkbox]:checked")
        .forEach((cb) => devicesMarcados.add(cb.value));
      fecharModal();
      void aplicarComTratamentoDeErro();
    });
  }

  container.querySelector("#sd-abrir-modal")!.addEventListener("click", () => void abrirModalVariaveisDevices());
  container.querySelector("#sd-filtrar")!.addEventListener("click", () => {
    void aplicarComTratamentoDeErro();
  });

  await aplicarComTratamentoDeErro();
}
